import { Divider, ListItemText, styled } from "~/common/mui";
import type React from "react";
import { forwardRef, Fragment } from "react";
import { RadiusSelect } from "~/features/salesPartner/components/SearchCard/GooglePlacesInput/RadiusSelect";
import { SearchType } from "~/domains/salesPartner/api/getSalesPartnerSearch";
import { useTranslation } from "react-i18next";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { LinkButton } from "~/common/components/Link";
import { SearchInputGroup } from "~/common/components/SearchInputGroup";

export interface GroupedSelectRef {
  reset: () => void;
}

export interface Suggestion {
  label: string;
  group: string;
  placeID?: string;
  searchType?: SearchType;
  searchQuery?: string;
}

interface GroupedSelectProps {
  disabled?: boolean;
  openFilterDialog: () => void;
  showFilterButton: boolean;
  selectedOption: Suggestion | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<Suggestion | null>>;
  suggestedCombinedList: Suggestion[];
  onSearchInputChange: (input: string) => void;
  radiusValue?: number;
  onRadiusChange?: (value: number) => void;
}

const GroupHeader = styled("div")(({ theme }) => ({
  position: "sticky",
  padding: theme.spacing(1),
  paddingTop: theme.spacing(2),
  color: theme.palette.text.label,
  backgroundColor: theme.palette.surface.main,
  textTransform: "capitalize",
  ...getFontStyle(FontSize.BodySmall),
}));

const GroupItems = styled("ul")(({ theme }) => ({
  padding: 0,
  backgroundColor: theme.palette.surface.main,
  ...getFontStyle(FontSize.BodyLarge),
}));

const GroupedSelectContainerWithFilter = styled("div")({
  display: "flex",
});

const GroupedSelectContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flex: 1,
  borderRadius: 28,
  backgroundColor: theme.palette.surface.main,
  overflow: "hidden",
  alignItems: "center",
}));

const FilterLinkButton = styled(LinkButton)(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const FilterIconContainer = styled("div")(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.secondary.container,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const GroupedSelect = forwardRef<GroupedSelectRef, GroupedSelectProps>((props, ref) => {
  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const {
    disabled,
    openFilterDialog,
    showFilterButton,
    selectedOption,
    suggestedCombinedList,
    onSearchInputChange,
    setSelectedOption,
    radiusValue,
    onRadiusChange,
  } = props;

  const { t } = useTranslation();

  const autocompleteCombinedInput = (clickable: boolean) => (
    <GroupedSelectContainer>
      <SearchInputGroup.Autocomplete
        disabled={disabled}
        fullWidth
        value={selectedOption}
        filterOptions={(x) => x} // skip MUI's default filter
        options={suggestedCombinedList}
        open={clickable ? false : undefined}
        popupIcon={clickable ? null : undefined}
        groupBy={(option) => (typeof option === "string" ? "" : option.group)}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader>{params.group}</GroupHeader>
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
        renderOption={(props, option, { index }) => (
          <Fragment key={option.label}>
            <li {...props}>
              <ListItemText primary={option.label} />
            </li>
            <Divider />
          </Fragment>
        )}
        placeholder={t("partnerFinder.search.placeholder")}
        onInputChange={(e, newValue) => {
          onSearchInputChange(newValue);
        }}
        onChange={(event, newValue) => {
          setSelectedOption(newValue);
        }}
      />
      {!isMobile && (
        <>
          <SearchInputGroup.Divider />
          <RadiusSelect
            disabled={typeof selectedOption !== "string" && selectedOption?.group == SearchType.Partner}
            value={radiusValue ?? 100}
            onChange={onRadiusChange ?? (() => {})}
          />
        </>
      )}
    </GroupedSelectContainer>
  );

  const searchClickableButton = (
    <FilterLinkButton onClick={openFilterDialog}>
      {isMobile && autocompleteCombinedInput(true)}
      <FilterIconContainer>
        <FilterAltOutlinedIcon
          fontSize="medium"
            sx={{
            color: "secondaryContainer.contrastText",
          }}
        />
      </FilterIconContainer>
    </FilterLinkButton>
  );

  return (
    <GroupedSelectContainerWithFilter>
      {autocompleteCombinedInput(false)}
      {showFilterButton && isMobile && searchClickableButton}
    </GroupedSelectContainerWithFilter>
  );
});

export default GroupedSelect;
